import React, { useState } from 'react';
import { Button, Card, Input, Select, Space, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import AssessmentTemplateModal from './AssessmentTemplateModal';
import { useRecruitList } from './shared/useRecruitList';
import { exportRows, fmtDateTime, parseJsonArray, stageLabel, useRecruitAccess } from './shared/recruitUi';

const FILTERS = ['tab', 'status'] as const;

/** Assessment instances are full pages (/new, /:id); scorecard templates are a master edited in a popup. */
const AssessmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { canManage } = useRecruitAccess();
  const list = useRecruitList(FILTERS);
  const tab = list.filters.tab === 'templates' ? 'templates' : 'instances';
  const [tplModal, setTplModal] = useState<{ open: boolean; row?: any }>({ open: false });

  const params = { ...list.apiParams, tab: undefined };
  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.assessments(params),
    queryFn: () => hrmsService.pageAssessments(params),
    placeholderData: keepPreviousData,
    enabled: tab === 'instances',
  });
  const { data: templates = [], isFetching: tplLoading } = useQuery({
    queryKey: hrmsKeys.assessmentTemplates(),
    queryFn: () => hrmsService.listAssessmentTemplates(),
  });

  const exportAll = async () => {
    const all = await hrmsService.pageAssessments({ ...params, page: 1, limit: 5000 });
    await exportRows(
      'assessments',
      [
        ['assessment_number', 'Assessment #'],
        ['title', 'Title'],
        ['candidate_name', 'Candidate'],
        ['application_number', 'Application'],
        ['job_title', 'Job'],
        ['scheduled_at', 'Scheduled'],
        ['percentage_score', 'Score %'],
        ['pass_fail', 'Result'],
        ['status', 'Status'],
      ],
      all.rows
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Assessment #',
      dataIndex: 'assessment_number',
      width: 140,
      render: (v: string, r) => <a onClick={() => navigate(`/hr/assessments/${r.id}`)}>{v}</a>,
    },
    { title: 'Title', dataIndex: 'title', ellipsis: true },
    { title: 'Candidate', dataIndex: 'candidate_name', ellipsis: true },
    { title: 'Job', dataIndex: 'job_title', ellipsis: true },
    { title: 'Scheduled', dataIndex: 'scheduled_at', width: 160, render: (v) => fmtDateTime(v) },
    { title: 'Score', dataIndex: 'percentage_score', width: 90, render: (v) => (v != null ? `${Number(v)}%` : '—') },
    { title: 'Result', dataIndex: 'pass_fail', width: 90, render: (v: string) => (v ? <Tag color={v === 'Pass' ? 'green' : 'red'}>{v}</Tag> : '—') },
    { title: 'Status', dataIndex: 'status', width: 110, render: (s: string) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Assessments"
          subtitle="Competency, technical and behavioural scorecards for hiring decisions"
          primaryActions={
            <Space>
              {tab === 'instances' && (
                <Button icon={<DownloadOutlined />} onClick={exportAll}>
                  Export
                </Button>
              )}
              {canManage && <Button onClick={() => setTplModal({ open: true })}>New template</Button>}
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/assessments/new')}>
                Assign assessment
              </Button>
            </Space>
          }
        />
      }
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => list.update({ tab: k === 'templates' ? 'templates' : undefined })}
        items={[
          {
            key: 'instances',
            label: 'Assessments',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <Input.Search allowClear placeholder="Number, title, candidate" defaultValue={list.q} onSearch={list.setQ} style={{ width: 240 }} />
                    <Select
                      allowClear
                      placeholder="Status"
                      style={{ width: 160 }}
                      value={list.filters.status}
                      onChange={(v) => list.setFilter('status', v)}
                      options={['Scheduled', 'InProgress', 'Completed', 'Cancelled'].map((v) => ({ value: v, label: stageLabel(v) }))}
                    />
                  </Space>
                </Card>
                <Table
                  rowKey="id"
                  loading={isFetching}
                  columns={columns}
                  dataSource={data?.rows || []}
                  scroll={{ x: 1000 }}
                  pagination={list.pagination(data?.total || 0)}
                  onChange={list.onTableChange}
                />
              </>
            ),
          },
          {
            key: 'templates',
            label: `Scorecard templates (${templates.length})`,
            children: (
              <Table
                rowKey="id"
                loading={tplLoading}
                dataSource={templates}
                pagination={false}
                columns={[
                  { title: 'Code', dataIndex: 'template_code', width: 150 },
                  { title: 'Name', dataIndex: 'template_name' },
                  { title: 'Type', dataIndex: 'assessment_type', width: 120 },
                  { title: 'Pass %', dataIndex: 'passing_score', width: 90, render: (v) => Number(v) },
                  { title: 'Criteria', key: 'q', width: 90, render: (_, r) => parseJsonArray(r.questions_json).length },
                  { title: 'Active', dataIndex: 'is_active', width: 90, render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
                  ...(canManage
                    ? [
                        {
                          title: '',
                          key: 'edit',
                          width: 80,
                          render: (_: unknown, r: any) => (
                            <Button size="small" type="link" icon={<EditOutlined />} onClick={() => setTplModal({ open: true, row: r })}>
                              Edit
                            </Button>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            ),
          },
        ]}
      />
      <AssessmentTemplateModal open={tplModal.open} template={tplModal.row} onClose={() => setTplModal({ open: false })} />
    </WorkspaceLayout>
  );
};

export default AssessmentsPage;
